import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { GetApiArbitrages200Item } from '@/api';
import Alert from '@/components/Alert/Alert';
import ExpiredProposalModal from '@/components/ExpiredProposalModal/ExpiredProposalModal';
import { Spinner } from '@/components/Spinner/Spinner';
import { ROUTE } from '@/constants/route';
import { STATUS } from '@/enums/status';
import useCheckArbitrageProposalStatus from '@/hooks/useCheckArbitrageProposalStatus';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import toTrackingSlug from '@/utils/functions/tracking/toTrackingSlug/toTrackingSlug';

import styles from './ArbitrageProposalBanner.module.css';

interface Props {
  proposals: GetApiArbitrages200Item[];
}

const ArbitrageProposalBanner = ({ proposals }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showExpiredModal, checkProposalStatus, closeExpiredModal, isPending } = useCheckArbitrageProposalStatus();

  if (!proposals.length) return null;

  const navigateToArbitrageSummary = (proposal: GetApiArbitrages200Item) => {
    navigate(generatePath(ROUTE.ARBITRAGESUMMARY, { id: proposal.arbitrageId ?? '' }), {
      state: {
        arbitrageId: proposal.arbitrageId,
        idPlacement: proposal.idPlacement,
      },
    });
  };

  const onClickViewProposal = (proposal: GetApiArbitrages200Item) => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'placement',
      event_nom: 'voir_la_proposition',
      event_detail_1: 'proposition_d_arbitrage',
      event_detail_2: toTrackingSlug(proposal.contractName),
    });
    checkProposalStatus(proposal.arbitrageId, () => navigateToArbitrageSummary(proposal));
  };

  return (
    <div className={styles.wrapper}>
      {showExpiredModal && <ExpiredProposalModal onClose={closeExpiredModal} />}
      {proposals.map((proposal) => (
        <Alert
          key={proposal.arbitrageId}
          status={STATUS.INFO}
          title={t('propositionArbitrageBanner.singleTitle', {
            contractName:
              (proposal.contractName ?? '').charAt(0).toUpperCase() + (proposal.contractName ?? '').slice(1),
          })}
          description={t('propositionArbitrageBanner.singleDescription')}
          labelButton={
            isPending ? (
              <div className={styles.spinnerWrapper}>
                <Spinner diameter={16} color="--totem-color-status-info-500" />
              </div>
            ) : (
              t('propositionArbitrageBanner.singleCta')
            )
          }
          onClickButton={() => onClickViewProposal(proposal)}
        />
      ))}
    </div>
  );
};

export default ArbitrageProposalBanner;
